import { IShopsData, IShopsAction } from 'models/shop/shops';
import { IStore } from 'redux/IStore';
import { IShopListItem } from 'models/shop/shoplistitem';
import { ShopStatus } from 'models';
import { nullCheck } from 'helpers/common';
import { IPage } from 'models/common';

/** Action Types */
export const REQUEST_START: string = 'shops/REQUEST_START';
export const REQUEST_FAILURE: string = 'shops/REQUEST_FAILURE';
export const GET_SHOPS_SUCCESS: string = 'shops/GET_SHOPS_SUCCESS';
export const SET_ITEMS_PAGE: string = 'shops/SET_ITEMS_PAGE';
export const SET_USER_ID: string = 'shops/SET_USER_ID';
export const RESET_DATA: string = 'shops/RESET_DATA';
export const SET_LAST_SEARCHED_KEYWORD: string = 'shops/SET_LAST_SEARCHED_KEYWORD';
export const SET_STATUS_FILTER: string = 'shops/SET_STATUS_FILTER';
export const SET_SEARCH_FILTER: string = 'shops/SET_SEARCH_FILTER';
export const SET_SORT_FILTER: string = 'shops/SET_SORT_FILTER';
export const SET_ISFIRSTLOAD: string = 'shops/SET_ISFIRSTLOAD';
export const RESET_FILTERS: string = 'shops/RESET_FILTERS';

/** Initial State */
const initialState: IShopsData = {
	items: [],
	isFetching: false,
	itemsPage: {
		currentPage: 0,
		totalPages: 0,
		totalItems: 0,
		itemsPerPage: 10,
		items: [],
	},
	keywordFilter: '',
	sortFilter: null,
	statusFilter: 'allshops',
	userId: null,
	lastSearchedKeyword: null,
	isFirstLoad: false,
};

/** Reducer */
export function shopsReducer(state = initialState, action: IShopsAction) {
	switch (action.type) {
		case REQUEST_START:
			return Object.assign({}, state, {
				isFetching: true,
				error: false,
			});

		case GET_SHOPS_SUCCESS:
			return Object.assign({}, state, {
				items: action.payload.items,
				isFetching: false,
			});

		case REQUEST_FAILURE:
			return Object.assign({}, state, {
				isFetching: false,
				message: action.payload.message,
				error: true,
			});

		case SET_ITEMS_PAGE:
			return Object.assign({}, state, {
				itemsPage: action.payload.itemsPage,
			});

		case RESET_DATA:
			return Object.assign({}, state, {
				items: [],
				itemsPage: {
					currentPage: 0,
					totalPages: 0,
					totalItems: 0,
					itemsPerPage: 10,
					items: [],
				},
				userId: null,
			});

		case RESET_FILTERS:
			return Object.assign({}, state, {
				keywordFilter: '',
				sortFilter: null,
				statusFilter: 'allposts',
			});

		case SET_STATUS_FILTER:
			return Object.assign({}, state, {
				keywordFilter: '',
				sortFilter: 0,
				statusFilter: action.payload.statusFilter,
				isFirstLoad: true,
			});

		case SET_SORT_FILTER:
			return Object.assign({}, state, {
				sortFilter: action.payload.sortFilter,
				isFirstLoad: true,
			});

		case SET_SEARCH_FILTER:
			return Object.assign({}, state, {
				keywordFilter: action.payload.keywordFilter,
				isFirstLoad: true,
			});

		case SET_ISFIRSTLOAD:
			return Object.assign({}, state, {
				isFirstLoad: action.payload.isFirstLoad,
			});

		case SET_USER_ID:
			return Object.assign({}, state, {
				userId: action.payload.userId,
			});

		case SET_LAST_SEARCHED_KEYWORD:
			return Object.assign({}, state, {
				lastSearchedKeyword: action.payload.lastSearchedKeyword,
			});

		default:
			return state;
	}
}

/** Action Creator */
export function getAdminShops() {
	return (dispatch, getState: () => IStore) => {
		if (getState().shops.isFirstLoad) {
			dispatch(resetData());
			dispatch(setIsFirstLoad(false));
		}

		dispatch(requestStart());

		const currentPage: number = getState().shops.itemsPage.currentPage;
		const newPage: number = currentPage + 1;
		const itemsPerPage: number = getState().shops.itemsPage.itemsPerPage;

		let queryString = '?';
		if (getState().shops.keywordFilter && getState().shops.keywordFilter !== '') {
			queryString = queryString + '&keyword=' + getState().shops.keywordFilter;
		}

		queryString = queryString + '&currentPage=' + newPage;
		queryString = queryString + '&itemsPerPage=' + itemsPerPage;
		queryString = queryString + '&sortColumn=' + mapAdminShopsSort(getState().shops.sortFilter);

		if (nullCheck(mapAdminShopsStatus(getState().shops.statusFilter))) {
			queryString = queryString + '&shopStatus=' + mapAdminShopsStatus(getState().shops.statusFilter);
		}

		return fetch(
			getState().fetchDefault.baseAPI + 'api/Shop/All' + queryString,
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'GET',
			}),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((res) => {
						const shopsState: IShopsData = getState().shops;
						const items: IShopListItem[] = shopsState.items;
						const newItems: IShopListItem[] = res.items;
						Array.prototype.push.apply(items, newItems);
						dispatch(getShopsSuccess(items));
						dispatch(setItemsPage(res));
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

function mapAdminShopsStatus(status: string): string {
	let mappedStatus = null;
	switch (status) {
		case 'allposts':
			mappedStatus = null;
			break;
		case 'approved':
			mappedStatus = ShopStatus.Approved;
			break;
		case 'pending':
			mappedStatus = ShopStatus.InApproval;
			break;
		case 'drafts':
			mappedStatus = ShopStatus.Draft;
			break;

		default:
			mappedStatus = null;
	}
	return mappedStatus;
}

function mapAdminShopsSort(sort: number): string {
	let mappedSort = 'AddedDate DESC';
	switch (sort) {
		case 0:
			mappedSort = 'AddedDate DESC';
			break;
		case 1:
			mappedSort = 'AddedDate ASC';
			break;
		default:
			mappedSort = 'AddedDate DESC';
	}
	return mappedSort;
}

/** Action Creator */
export function requestStart(): IShopsAction {
	return {
		type: REQUEST_START,
	};
}

/** Action Creator */
export function getShopsSuccess(items: IShopListItem[]): IShopsAction {
	return {
		type: GET_SHOPS_SUCCESS,
		payload: {
			items,
		},
	};
}

/** Action Creator */
export function requestFailure(message: any): IShopsAction {
	return {
		type: REQUEST_FAILURE,
		payload: {
			message,
		},
	};
}

/** Action Creator */
export function setItemsPage(itemsPage: IPage): IShopsAction {
	return {
		type: SET_ITEMS_PAGE,
		payload: {
			itemsPage,
		},
	};
}

/** Action Creator */
export function resetData(): IShopsAction {
	return {
		type: RESET_DATA,
	};
}

/** Action Creator */
export function setUserId(userId: string): IShopsAction {
	return {
		type: SET_USER_ID,
		payload: {
			userId,
		},
	};
}

/** Action Creator */
export function setLastSearchedKeyword(lastSearchedKeyword: string): IShopsAction {
	return {
		type: SET_LAST_SEARCHED_KEYWORD,
		payload: {
			lastSearchedKeyword,
		},
	};
}

export function setStatusFilter(statusFilter: string): IShopsAction {
	return {
		type: SET_STATUS_FILTER,
		payload: {
			statusFilter,
		},
	};
}

export function setSearchFilter(keywordFilter: string): IShopsAction {
	return {
		type: SET_SEARCH_FILTER,
		payload: {
			keywordFilter,
		},
	};
}

export function setSortFilter(sortFilter: number): IShopsAction {
	return {
		type: SET_SORT_FILTER,
		payload: {
			sortFilter,
		},
	};
}

export function resetFilters(): IShopsAction {
	return {
		type: RESET_FILTERS,
	};
}

export function setIsFirstLoad(isFirstLoad: boolean): IShopsAction {
	return {
		type: SET_ISFIRSTLOAD,
		payload: {
			isFirstLoad,
		},
	};
}
